// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Rum tool (R; frontend-u0o.6, slice S6; mock 05): Placér i lukket
 * område (a click inside the walls places the room they close around it —
 * `POST /spaces/rebuild` with `seed`, previewed under the pointer first),
 * Tegn polygon (a room drawn corner by corner — `space.create`), Del rum (a
 * line cut — `POST /spaces/split`), Fra vægge (the whole storey rebuilt from
 * its walls, shown as a diff before Anvend) and Flet rum (`POST
 * /spaces/merge`). What each previews and how the banner sums it up, the
 * polygon's checks, the create op, a draft as a drawable space, and the
 * server's 422 reasons in Danish. Pure: no React, no DOM.
 */

import type { ModelEditOp, PlanXY, SolidSpace, SpaceDraft, Storey } from '../api/types';
import { ringSelfIntersects, spaceLabel, type GeometriModel } from './geometri';
import type { XY } from './planWalls';
import { formatAreaDa, ringArea } from './spaces';

export type RoomMode = 'place' | 'polygon' | 'split';

export interface RoomOptions {
  mode: RoomMode;
  /** The name a new room gets; '' = the next free "Rum N". */
  name: string;
  kind: SolidSpace['kind'];
  /** The Zone tool's new zone: its name ('' = the next "Zone N") and use. */
  zoneName: string;
  zoneUse: string;
}

export const DEFAULT_ROOM_OPTIONS: RoomOptions = { mode: 'place', name: '', kind: 'room', zoneName: '', zoneUse: '' };

/** The smallest room the tool makes, m². */
export const MIN_ROOM_AREA = 0.25;

/** "Rum 1", "Rum 2", … the first number no room's name uses. */
export function nextRoomName(spaces: readonly Pick<SolidSpace, 'name'>[] | null | undefined): string {
  const taken = new Set((spaces ?? []).map((s) => s.name.trim().toLowerCase()));
  for (let n = 1; ; n += 1) if (!taken.has(`rum ${n}`)) return `Rum ${n}`;
}

const r6 = (v: number) => Math.round(v * 1e6) / 1e6;

// ---------------------------------------------------------- polygon --

/** Two plan points this close (m) are one. */
const SAME = 1e-3;

/** Whether a click at `p` closes the polygon: on its first corner (within `tol` metres), with three corners down. */
export function closesPolygon(points: readonly XY[], p: XY, tol: number): boolean {
  return points.length >= 3 && Math.hypot(p[0] - points[0][0], p[1] - points[0][1]) <= tol;
}

/** The corners as drawn: repeats (a double click) and a closing repeat of the first dropped. */
export function cleanRing(points: readonly XY[]): XY[] {
  const out: XY[] = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (last && Math.hypot(p[0] - last[0], p[1] - last[1]) < SAME) continue;
    out.push(p);
  }
  while (out.length > 1 && Math.hypot(out[0][0] - out[out.length - 1][0], out[0][1] - out[out.length - 1][1]) < SAME) out.pop();
  // Corners on a straight line add nothing.
  for (let i = 0; out.length > 3 && i < out.length; ) {
    const a = out[(i + out.length - 1) % out.length];
    const b = out[i];
    const c = out[(i + 1) % out.length];
    const cross = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
    if (Math.abs(cross) < 1e-9) out.splice(i, 1);
    else i += 1;
  }
  return out;
}

/** Why a drawn ring cannot be a room (Danish), or null when it can. */
export function ringProblem(ring: readonly XY[]): string | null {
  if (ring.length < 3) return 'Et rum skal have mindst tre hjørner';
  if (ringSelfIntersects(ring)) return 'Omridset krydser sig selv';
  if (Math.abs(ringArea(ring)) < MIN_ROOM_AREA) return `Rummet er for lille (under ${formatAreaDa(MIN_ROOM_AREA)})`;
  return null;
}

/** A ring counter-clockwise (the model's outer rings are), rounded to the micrometre. */
export function ccw(ring: readonly XY[]): PlanXY[] {
  const r = ringArea(ring) < 0 ? [...ring].reverse() : [...ring];
  return r.map((p) => [r6(p[0]), r6(p[1])] as PlanXY);
}

/** The storey's floor and ceiling (world z) a new room on it takes. */
export function storeyHeights(storeys: readonly Storey[] | null | undefined, storeyId: number): { z_floor: number; z_ceiling: number } {
  const s = storeys?.find((x) => x.id === storeyId);
  const zf = s?.z_min ?? 0;
  const zc = s && s.z_max > s.z_min + 0.5 ? s.z_max : zf + 2.5;
  return { z_floor: r6(zf), z_ceiling: r6(zc) };
}

/** Tegn polygon: the `space.create` of a drawn room, or why not. */
export function createRoomOps(
  model: GeometriModel,
  points: readonly XY[],
  storeyId: number,
  opts: Pick<RoomOptions, 'name' | 'kind'>,
  guid: string,
): { ops: ModelEditOp[]; label: string } | { error: string } {
  const ring = cleanRing(points);
  const problem = ringProblem(ring);
  if (problem) return { error: problem };
  const name = opts.name.trim() || nextRoomName(model.spaces);
  return {
    ops: [
      {
        op: 'space.create',
        record: { guid, storey_id: storeyId, name, kind: opts.kind, polygon: { outer: ccw(ring), holes: [] }, ...storeyHeights(model.storeys, storeyId) },
      },
    ],
    label: `Nyt rum ${name}`,
  };
}

// ----------------------------------------------------------- drafts --

/** A server's draft as a space the plan can draw (the preview's own row). */
export function draftSpace(d: SpaceDraft, id = -1): SolidSpace {
  const kind = d.kind === 'room' || d.kind === 'corridor' || d.kind === 'open_plan' ? d.kind : 'unknown';
  return {
    id,
    guid: d.guid,
    source: 'manual',
    locked: true,
    confidence: null,
    metadata: null,
    name: d.name,
    kind,
    storey_id: d.storey_id,
    storey_guid: null,
    polygon: { outer: d.polygon.outer, holes: d.polygon.holes ?? [] },
    z_floor: d.z_floor,
    z_ceiling: d.z_ceiling,
    area: d.area,
    perimeter: d.perimeter,
    volume: d.volume ?? null,
    room_label: null,
  };
}

/** Whether two outlines are the same (within a centimetre, same corners in order, any start). */
export function samePolygon(a: SpaceDraft['polygon'] | SolidSpace['polygon'], b: SpaceDraft['polygon'] | SolidSpace['polygon']): boolean {
  if (a.outer.length !== b.outer.length || (a.holes?.length ?? 0) !== (b.holes?.length ?? 0)) return false;
  const n = a.outer.length;
  const near = (p: readonly number[], q: readonly number[]) => Math.abs(p[0] - q[0]) < 0.01 && Math.abs(p[1] - q[1]) < 0.01;
  for (let k = 0; k < n; k += 1) {
    if (a.outer.every((p, i) => near(p, b.outer[(i + k) % n]))) return true;
  }
  return false;
}

export type DraftState = 'new' | 'changed' | 'same';

export interface RebuildDiff {
  /** The drafts with what each does to the stored room it takes. */
  rows: { draft: SpaceDraft; state: DraftState; /** The stored room it takes (by name), null for a new one. */ was: string | null }[];
  /** Stored rooms of the storey no draft takes (kept unless removed). */
  unmatched: SolidSpace[];
  added: number;
  changed: number;
  same: number;
  /** "5 rum · 1 nyt · 1 ændret · navne bevaret". */
  summary: string;
}

/** Fra vægge's preview against the storey's stored rooms: new, changed, unchanged; the stored ones left over. */
export function rebuildDiff(model: GeometriModel, storeyId: number, drafts: readonly SpaceDraft[]): RebuildDiff {
  const stored = new Map((model.spaces ?? []).filter((s) => s.storey_id === storeyId).map((s) => [s.guid, s]));
  const rows = drafts.map((d) => {
    const s = stored.get(d.guid);
    const state: DraftState = !s ? 'new' : samePolygon(s.polygon, d.polygon) ? 'same' : 'changed';
    return { draft: d, state, was: s ? spaceLabel(s) : null };
  });
  const taken = new Set(drafts.map((d) => d.guid));
  const unmatched = [...stored.values()].filter((s) => !taken.has(s.guid));
  const added = rows.filter((r) => r.state === 'new').length;
  const changed = rows.filter((r) => r.state === 'changed').length;
  const same = rows.length - added - changed;
  const parts = [`${drafts.length} rum`];
  if (added > 0) parts.push(added === 1 ? '1 nyt' : `${added} nye`);
  if (changed > 0) parts.push(`${changed} ændret`);
  if (added === 0 && changed === 0) parts.push('ingen ændringer');
  if (unmatched.length > 0) parts.push(`${unmatched.length} uden vægge beholdes`);
  if (rows.some((r) => r.state !== 'new')) parts.push('navne bevaret');
  return { rows, unmatched, added, changed, same, summary: parts.join(' · ') };
}

/** Placér's preview: "Nyt rum · 14,4 m²", or "Tilpas Køkken · 21,4 m²" for a stored room re-fitted. */
export function placeSummary(draft: SpaceDraft, existing: boolean): string {
  const area = typeof draft.area === 'number' ? ` · ${formatAreaDa(draft.area)}` : '';
  return existing ? `Tilpas ${draft.name.trim() || 'rummet'} til væggene${area}` : `Nyt rum${area}`;
}

/** Flet rum's preview banner. */
export function mergeSummary(model: GeometriModel, a: string, b: string, draft: SpaceDraft): string {
  const name = (g: string) => {
    const s = model.spaces?.find((x) => x.guid === g);
    return s ? spaceLabel(s) : 'rummet';
  };
  return `${name(b)} flettes ind i ${name(a)}${typeof draft.area === 'number' ? ` · ${formatAreaDa(draft.area)}` : ''}`;
}

/** Del rum's preview banner: "Køkken deles i 2: 14,0 m² og 7,4 m²". */
export function splitSummary(model: GeometriModel, guid: string, drafts: readonly SpaceDraft[]): string {
  const s = model.spaces?.find((x) => x.guid === guid);
  const areas = drafts.map((d) => (typeof d.area === 'number' ? formatAreaDa(d.area) : '?'));
  const list = areas.length <= 1 ? areas.join('') : `${areas.slice(0, -1).join(', ')} og ${areas[areas.length - 1]}`;
  return `${s ? spaceLabel(s) : 'Rummet'} deles i ${drafts.length}: ${list}`;
}

/** The selection's rooms (in order). */
export function selectedRooms(model: GeometriModel, selection: readonly string[]): SolidSpace[] {
  const byGuid = new Map((model.spaces ?? []).map((s) => [s.guid, s]));
  return selection.map((g) => byGuid.get(g)).filter((s): s is SolidSpace => s !== undefined);
}

// ------------------------------------------------------------ errors --

const REASONS: [RegExp, string][] = [
  [/lies in wall/i, 'Punktet ligger i en væg — klik inde i et rum'],
  [/no closed area/i, 'Intet lukket område her — luk væggene, eller tegn rummet som polygon'],
  [/neither touch nor share a wall/i, 'Rummene hverken rører hinanden eller deler en væg'],
  [/are on storeys|different storeys/i, 'Rummene ligger på hver sin etage'],
  [/merged with itself/i, 'Et rum kan ikke flettes med sig selv'],
  [/does not cut/i, 'Linjen deler ikke rummet i to — træk den helt over rummet'],
  [/line has no length/i, 'Linjen har ingen længde'],
  [/not simple/i, 'Rummets omrids krydser sig selv'],
  [/has no polygon/i, 'Rummet har intet omrids'],
  [/close no space|closes? no space|no space from/i, 'Væggene lukker ingen rum på etagen'],
];

/** A room tool's refusal (422) in Danish; the server's own words when they are not known. */
export function roomToolError(message: string | undefined): string {
  const m = message ?? '';
  for (const [re, da] of REASONS) if (re.test(m)) return da;
  return m ? `Rummet kunne ikke laves: ${m}` : 'Rummet kunne ikke laves';
}

/**
 * The model ops a room tool's result amounts to, for the optimistic
 * overlay while it is applied (the re-read wins): each draft an update of
 * the stored room it keeps (or a create), and the rooms it removes deleted.
 */
export function draftOps(model: GeometriModel, drafts: readonly SpaceDraft[], removed: readonly string[] = []): ModelEditOp[] {
  const stored = new Set((model.spaces ?? []).map((s) => s.guid));
  const ops: ModelEditOp[] = [];
  for (const d of drafts) {
    const fields = { name: d.name, kind: d.kind, storey_id: d.storey_id, polygon: d.polygon, z_floor: d.z_floor, z_ceiling: d.z_ceiling, area: d.area, perimeter: d.perimeter, volume: d.volume ?? null };
    if (stored.has(d.guid)) ops.push({ op: 'space.update', guid: d.guid, set: fields });
    else ops.push({ op: 'space.create', record: { guid: d.guid, room_label: null, storey_guid: null, ...fields } });
  }
  for (const g of removed) if (stored.has(g)) ops.push({ op: 'space.delete', guid: g });
  return ops;
}
